import * as React from 'react';
import { EErrorCode } from 'config/errors-codes';
import { errorCollection } from 'config/ui/error-collection';
import type { IBrand } from 'data/schema';
import { useError } from 'hooks/useError';
import {
  useBandwidthOptimisation,
  BandwidthOptimisationConfig as BandwidthOptimisationConfig,
} from 'hooks/useBandwidthOptimisation';
import { endSession, userSession } from 'state/session';
import { startNewSession } from 'state/session';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { injectShowroom } from 'utils/ui/showroom';
import { getStreamingStage, StreamingStage } from 'utils/ui/stage';

export type DeckAttributes = React.HTMLAttributes<HTMLElement> & {
  active?: boolean;
  'pr-string'?: string;
  'camera-flight'?: 'disabled' | 'enabled';
  'config-id': string;
  'hide-consumption-data'?: boolean;
  camera?: string;
  market: string;
  style?: React.CSSProperties | string;
  ref?: React.RefObject<HTMLElement | null>;
  features?: string;
  'streaming-stage'?: StreamingStage;
};

export type IShowroomProps = {
  market: string;
  brand: IBrand;
  active?: boolean;
  prString?: string;
  hideConsumptionData?: boolean;
  camera?: string;
  cameraFlight?: 'enabled' | 'disabled';
  sessionId?: string;
  className?: string;
  userType?: 'dealer' | 'customer';
  customerAllowance?: boolean;
  features?: Partial<IShowroomFeatureFlags>;
  bandwidthOptimisation?: BandwidthOptimisationConfig & {
    enabled?: boolean;
  };
  onConnected?: () => void;
  onError?: (event: IShowroomErrorEvent, showroomErrorData: Record<string, unknown>) => boolean | void;
  onActiveChange?: (active: boolean) => void;
  onUpdateConsumptionData?: (wltpText?: string) => void;
  onUpdatePrString?: (prString?: string) => void;
  onTimeout?: () => void;
};

export interface IShowroomState {
  active: boolean;
  sessionId?: string;
}

// This type is extracted from Showroom directly
export type IShowroomErrorEvent = {
  type: 'exception';
  message: string;
  error: Error;
};

export interface IShowroomFeatureFlags {
  close?: boolean;
  cameraNavigation?: boolean;
  animations?: boolean;
  fullscreen?: boolean;
  cameraFlight?: boolean;
  animationSpots?: boolean;
  interior?: boolean;
  environments?: boolean;
  zoom?: boolean;
}

export function Showroom(props: IShowroomProps) {
  const {
    active = true,
    market,
    brand,
    prString,
    hideConsumptionData,
    camera,
    cameraFlight = 'disabled',
    sessionId,
    className = '',
    userType = user.type,
    customerAllowance = false,
    features,
    bandwidthOptimisation = { enabled: false },
    onConnected,
    onError,
    onUpdateConsumptionData,
    onUpdatePrString,
    onTimeout,
  } = props;

  const showroom = React.useRef<HTMLElement>(null);
  const hasListeners = React.useRef(false);
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const throwError = useError();

  const bandwidthState = useBandwidthOptimisation({
    enabled: bandwidthOptimisation.enabled,
    ...bandwidthOptimisation,
  });

  const shouldBeActive = bandwidthState.isManaged ? active && bandwidthState.shouldBeActive : active;

  const featureBlackListCustomer: IShowroomFeatureFlags = {
    close: false,
    cameraNavigation: customerAllowance,
    animations: customerAllowance,
    fullscreen: customerAllowance,
    cameraFlight: customerAllowance,
    animationSpots: customerAllowance,
    interior: customerAllowance,
    environments: customerAllowance,
    zoom: customerAllowance,
  };

  const featureBlacklist = {
    dealer: {
      close: false,
    },
    customer: featureBlackListCustomer,
  };

  React.useEffect(() => {
    if (showroom.current && !hasListeners.current) {
      hasListeners.current = true;
      showroom.current?.addEventListener('connected', (e) => {
        const aveSessionId = (e as CustomEvent).detail.sessionId;
        if (!sessionId) {
          console.warn('Starting new session with ID:', aveSessionId);
          startNewSession({ initialData: { aveSessionId: aveSessionId, streamActive: true } });
        }
        onConnected?.();
      });

      showroom.current?.addEventListener('disconnected', (e) => {
        /** Don't end the session when the stream is just hidden */
        if (bandwidthState.isManaged && active) {
          return;
        }
        const reason = (e as CustomEvent).detail.reason;
        if (reason === 'INACTIVITY_TIMEOUT') {
          onTimeout?.();
          logger.warn('Session ended due to inactivity timeout.');
          return endSession({ isTimeout: true });
        }
      });

      showroom.current?.addEventListener('exception', (e) => {
        let shouldShowErrorBoundary = true;
        const errorCode = processShowroomError(e as CustomEvent);

        if (onError && errorCode) {
          // if onError returns false, we don't show the error boundary
          shouldShowErrorBoundary = onError(e as unknown as IShowroomErrorEvent, errorCollection[errorCode]) !== false;
        }

        // Only show toasts for the standalone app
        if (!shouldShowErrorBoundary && integrationType() !== IntegrationType.WebComponent) {
          return;
        }

        // if no toast are shown or if the onError callback returned a value,
        // then we go back to the error boundary
        if (!shouldShowErrorBoundary) {
          throwError(EErrorCode.INTERNAL_ERROR, { message: (e as unknown as IShowroomErrorEvent).message });
        }
      });

      showroom.current?.addEventListener('update:consumption-data', (e) => {
        onUpdateConsumptionData?.((e as CustomEvent).detail?.value);
      });

      showroom.current?.addEventListener('update:pr-string', (e) => {
        onUpdatePrString?.((e as CustomEvent).detail?.value);
      });
    }

    // These dependencies are enough as we don't want to update events unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showroom, onConnected, scriptLoaded]);

  React.useEffect(() => {
    injectShowroom({ onLoad: setScriptLoaded });
  }, []);

  if (!scriptLoaded) {
    return null;
  }

  const isScheduledSession = !!userSession().get('scheduledStartTime');

  const showroomConfigs: Record<IBrand, string> = {
    A: isScheduledSession ? 'a-3DExhibition-scheduler-2025' : 'a-3DExhibition-default-2025',
    V: 'v-3DExhibition-default-2025',
    S: 'c-3DExhibition-default-2025',
    C: 'c-3DExhibition-default-2025',
    E: 'bentley-default',
    L: 'l-3DExhibition-default-2025',
    M: 'ngc-default',
    N: 'n-3DExhibition-default-2025',
    P: 'ngc-default',
    '*': 'ngc-default',
  };

  return (
    <avp-3dws-deck
      part="showroom"
      role="presentation"
      config-id={showroomConfigs[brand]}
      active={shouldBeActive}
      hide-consumption-data={hideConsumptionData}
      market={market === 'DEV' ? 'DEU' : market}
      pr-string={prString}
      camera={camera}
      camera-flight={cameraFlight}
      session-id={sessionId}
      /** Since this is a web component, the correct attribute is class, not className */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      class={className}
      ref={showroom}
      features={JSON.stringify({ ...featureBlacklist[userType], ...features })}
      streaming-stage={getStreamingStage(brand)}
    />
  );
}

function processShowroomError(error: CustomEvent<{ reason?: string; message?: string }>): EErrorCode | undefined {
  try {
    const errorCode = error.detail?.reason as keyof typeof EErrorCode;
    const errorMessage = error.detail?.message || '';

    const errorNumber = errorCode && EErrorCode[errorCode];

    if (errorNumber) {
      showToast({
        type: 'error',
        title: errorCollection[errorNumber].title(),
        message: errorCollection[errorNumber].message(),
        notificationId: errorCollection[errorNumber].notificationId,
      });
      return errorNumber;
    }

    if (errorMessage) {
      showToast({
        type: 'error',
        title: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].title(),
        message: errorMessage,
        notificationId: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].notificationId,
      });
      return EErrorCode.GENERAL_STREAM_ERROR;
    }

    showToast({
      type: 'error',
      title: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].title(),
      message: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].message(),
      notificationId: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].notificationId,
    });

    return EErrorCode.GENERAL_STREAM_ERROR;
  } catch {
    return EErrorCode.GENERAL_STREAM_ERROR;
  }
}
